import React, { useState, useEffect } from "react";
import { Wifi, WifiOff, Loader2, Copy, CheckCircle2, AlertTriangle, XCircle, ExternalLink, Link2, History, Radio, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import NayaxAutoMapDialog from "./NayaxAutoMapDialog";
import NayaxProductAutoMapDialog from "./NayaxProductAutoMapDialog";
import NayaxSyncLogsDialog from "./NayaxSyncLogsDialog";

/**
 * Bloc de diagnostic Nayax — affiché en haut de la page Distributeurs lorsque
 * le token n'est pas configuré OU que l'utilisateur veut vérifier sa connexion.
 *
 * Appelle `GET /api/nayax/test-connection` qui exécute en backend un `GET /devices`
 * sur l'API Lynx avec le token actuel. Retourne un diagnostic structuré + un
 * message FR clair (`token_missing`, `token_invalid`, `network_error`, `ok`).
 *
 * En cas de succès, expose les MachineIDs trouvés côté Nayax pour que
 * l'opérateur puisse copier-coller le bon ID dans le champ `nayax_machine_id`
 * du distributeur.
 */
export default function NayaxDiagnostic({ configured, env, onAfterMapping }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [devices, setDevices] = useState([]);
  const [autoMapOpen, setAutoMapOpen] = useState(false);
  const [productAutoMapOpen, setProductAutoMapOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [testDeviceId, setTestDeviceId] = useState("");
  const [testActorId, setTestActorId] = useState("");

  // Phase 101bb (14/02/2026) — Mode expert Nayax push picklist (toggle Settings).
  // Par défaut désactivé : les boutons de push manuel sont masqués pour éviter
  // de déclencher accidentellement le PUT picklist Nayax (source de HTTP 500).
  const [expertPushMode, setExpertPushMode] = useState(false);
  useEffect(() => {
    let mounted = true;
    api.get("/settings").then((r) => {
      if (mounted) setExpertPushMode(Boolean(r.data?.nayax_expert_push_mode));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Phase 98ak — Rattrapage sync Nayax MOMA (dernière tournée par distributeur)
  const [resyncConfirmOpen, setResyncConfirmOpen] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncReport, setResyncReport] = useState(null);
  const [resyncReportOpen, setResyncReportOpen] = useState(false);

  // Phase 101ay — Debug push_picklist 1-par-1 (isolation produit fautif Nayax 500)
  const [debugRunning, setDebugRunning] = useState(false);
  const [debugRunningFor, setDebugRunningFor] = useState(null); // distributor_id en cours
  const [debugReport, setDebugReport] = useState(null);
  const [debugReportOpen, setDebugReportOpen] = useState(false);

  const runDebugOneByOne = async (distributorId, tourId) => {
    setDebugRunning(true);
    setDebugRunningFor(distributorId);
    try {
      const qs = tourId ? `?tour_id=${encodeURIComponent(tourId)}` : "";
      const r = await api.post(`/nayax/debug-push-picklist/${distributorId}${qs}`);
      setDebugReport(r.data);
      setDebugReportOpen(true);
      const { failed_count, ok_count, total_tested } = r.data;
      if (failed_count === 0 && ok_count > 0) {
        toast.success(`Debug OK : les ${ok_count} produit(s) passent en 1-par-1.`);
      } else if (failed_count > 0) {
        toast.warning(`${failed_count} produit(s) fautif(s) isolé(s) sur ${total_tested}. Voir le rapport.`);
      } else {
        toast.info("Aucun produit testable — voir le rapport.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du debug 1-par-1");
    } finally {
      setDebugRunning(false);
      setDebugRunningFor(null);
    }
  };

  const runResyncAllMomas = async () => {
    setResyncConfirmOpen(false);
    setResyncing(true);
    try {
      const r = await api.post("/nayax/resync-all-momas-latest-tour");
      setResyncReport(r.data);
      setResyncReportOpen(true);
      const { total_distributors, success_count, skipped_count, failed_count } = r.data;
      if (total_distributors === 0) {
        toast.info("Aucun distributeur AUTOMATIQUE avec nayax_machine_id configuré.");
      } else if (failed_count === 0 && success_count > 0) {
        toast.success(`Rattrapage OK : ${success_count} distributeur(s) synchronisé(s), ${skipped_count} ignoré(s).`);
      } else if (success_count > 0) {
        toast.warning(`Rattrapage partiel : ${success_count} OK / ${failed_count} en échec / ${skipped_count} ignoré(s).`);
      } else if (skipped_count === total_distributors) {
        // Cas spécifique : tous ignorés à cause du mapping produit manquant
        const rs = r.data.results || [];
        const allMappingIssue = rs.length > 0 && rs.every((x) => (x.missing_mapping?.length || 0) > 0);
        if (allMappingIssue) {
          toast.warning(`Tous ignorés — champ nayax_product_id manquant sur les produits. Voir détails et lien vers /produits.`);
        } else {
          toast.info(`Tous ignorés (${skipped_count}) — voir détails.`);
        }
      } else {
        toast.error(`Rattrapage échoué : ${failed_count} distributeur(s) en erreur.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du rattrapage Nayax MOMA");
    } finally {
      setResyncing(false);
    }
  };

  const runTest = async ({ deviceId = null, actorId = null } = {}) => {
    setRunning(true);
    setResult(null);
    setDevices([]);
    try {
      const params = {};
      if (deviceId) params.device_id = String(deviceId).trim();
      if (actorId) params.actor_id = String(actorId).trim();
      const r = await api.get("/nayax/test-connection", { params });
      setResult(r.data);
      if (r.data.status === "ok" && Array.isArray(r.data.sample_devices) && r.data.sample_devices.length > 0) {
        setDevices(r.data.sample_devices);
      } else if (r.data.status === "ok" && r.data.sample_machine_id) {
        setDevices([{ id: r.data.sample_machine_id, name: "" }]);
      }
    } catch (e) {
      setResult({
        status: "unexpected",
        message: e?.response?.data?.detail || e?.message || "Erreur inconnue",
        raw_excerpt: "",
      });
    } finally {
      setRunning(false);
    }
  };

  const copy = (val) => {
    navigator.clipboard.writeText(String(val)).then(() => toast.success("Machine ID copié"));
  };

  const envLabel = (env || result?.env || "prod").toUpperCase();
  const envBg = envLabel === "QA"
    ? "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]"
    : "bg-[#E8EEF6] text-[#1F3A66] border-[#A8BBD6]";

  const statusBadge = () => {
    if (configured) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6] px-2 py-0.5 rounded">
          <Wifi className="w-3 h-3" /> Configuré
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#FBF4E9] text-[#8C6B2F] border border-[#E8D3A8] px-2 py-0.5 rounded">
        <WifiOff className="w-3 h-3" /> Token absent
      </span>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    const isWarning = result.status === "token_missing" || result.status === "wrong_endpoint" || result.status === "permission_denied";
    const icon = result.status === "ok" ? (
      <CheckCircle2 className="w-5 h-5 text-[#1F4E3D] flex-shrink-0" />
    ) : isWarning ? (
      <AlertTriangle className="w-5 h-5 text-[#8C6B2F] flex-shrink-0" />
    ) : (
      <XCircle className="w-5 h-5 text-[#C05C47] flex-shrink-0" />
    );
    const bg = result.status === "ok"
      ? "bg-[#E8F2EC] border-[#A8C9B6]"
      : isWarning
        ? "bg-[#FBF4E9] border-[#E8D3A8]"
        : "bg-[#FBEEEA] border-[#E8B0A0]";
    return (
      <div
        className={`mt-3 p-3 rounded border flex items-start gap-2 text-xs ${bg}`}
        data-testid={`nayax-test-result-${result.status}`}
      >
        {icon}
        <div className="flex-1 min-w-0">
          <p className="text-[#1A1C1A] leading-snug">
            {result.message}
            {result.http_status ? (
              <span className="ml-1 text-[10px] font-mono text-[#575A57]">(HTTP {result.http_status})</span>
            ) : null}
          </p>
          {result.base_url && (
            <p className="text-[10px] text-[#575A57] mt-1 font-mono break-all">
              {result.base_url}
            </p>
          )}
          {devices.length > 0 && (
            <div className="mt-2 pt-2 border-t border-current/10">
              <p className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1">
                Machines Nayax détectées ({devices.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {devices.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => copy(d.id)}
                    className="inline-flex items-center gap-1.5 font-mono text-xs bg-white px-2 py-1 rounded border border-[#E5E5E0] hover:bg-[#F4F4F0] transition-colors"
                    title="Copier dans le presse-papier"
                    data-testid="nayax-machine-id-copy"
                  >
                    {d.id}
                    {d.name ? <span className="text-[#575A57] font-sans text-[10px]">— {d.name}</span> : null}
                    <Copy className="w-3 h-3 text-[#A3A6A3]" />
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#575A57] mt-1.5">
                Collez-le dans le champ « ID Machine Nayax » du distributeur correspondant pour activer la synchro.
              </p>
            </div>
          )}
          {result.raw_excerpt && result.status !== "ok" && (
            <details className="mt-2">
              <summary className="text-[10px] uppercase tracking-wider text-[#575A57] cursor-pointer">
                Réponse brute Nayax
              </summary>
              <pre className="mt-1 p-2 bg-white border border-[#E5E5E0] rounded font-mono text-[10px] overflow-x-auto whitespace-pre-wrap break-all">
                {result.raw_excerpt}
              </pre>
            </details>
          )}
          {Array.isArray(result.schemes_tried) && result.schemes_tried.length > 0 && (
            <details className="mt-2" open={result.status !== "ok"}>
              <summary className="text-[10px] uppercase tracking-wider text-[#575A57] cursor-pointer">
                Détail technique ({result.schemes_tried.length} tentative{result.schemes_tried.length > 1 ? "s" : ""}
                {result.auth_scheme ? ` — schéma utilisé : ${result.auth_scheme}` : ""}
                {result.api_path ? ` — path : ${result.api_path}` : ""})
              </summary>
              <div className="mt-1 space-y-1">
                {result.schemes_tried.map((t, i) => (
                  <div
                    key={i}
                    className="p-1.5 bg-white border border-[#E5E5E0] rounded font-mono text-[10px]"
                    data-testid={`nayax-scheme-attempt-${t.scheme}`}
                  >
                    <span className="font-semibold">
                      {t.scheme === "bearer" ? "Bearer <token>" : "<token> raw"}
                    </span>
                    {t.api_path ? (
                      <span className="text-[#575A57]"> @ {t.api_path}</span>
                    ) : null}{" "}
                    →{" "}
                    {t.http_status ? (
                      <span className={
                        t.is_html_fallback
                          ? "text-[#8C6B2F]"
                          : t.http_status < 400 ? "text-[#1F4E3D]" : "text-[#C05C47]"
                      }>
                        HTTP {t.http_status}
                        {t.is_html_fallback ? " (HTML, pas JSON)" : ""}
                      </span>
                    ) : (
                      <span className="text-[#8C6B2F]">erreur réseau : {t.network_error}</span>
                    )}
                    {t.headers && Object.keys(t.headers).length > 0 && (
                      <div className="mt-0.5 text-[#575A57] break-all">
                        {Object.entries(t.headers).map(([k, v]) => (
                          <div key={k}>
                            <span className="font-semibold">{k}:</span> {v}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="border border-[#E5E5E0] rounded-md p-4 bg-white" data-testid="nayax-diagnostic">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Wifi className="w-4 h-4 text-[#575A57]" />
          <div>
            <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
              Intégration Nayax Lynx {statusBadge()}
              <span
                className={`inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${envBg}`}
                title="Environnement actif (NAYAX_ENV)"
                data-testid="nayax-env-badge"
              >
                Env: {envLabel}
              </span>
            </p>
            <p className="text-[11px] text-[#575A57] mt-0.5">
              Synchronisation automatique des ventes machines via l&apos;API Lynx (Bearer token).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {configured && (
            <>
              {expertPushMode && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setResyncConfirmOpen(true)}
                  disabled={resyncing}
                  variant="outline"
                  data-testid="nayax-resync-all-btn"
                  title="Mode expert — Rejouer la sync MOMA de la dernière tournée pour tous les distributeurs configurés (PUT picklist Nayax)"
                  className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                >
                  {resyncing ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Rattrapage…</>
                  ) : (
                    <><Radio className="w-3.5 h-3.5 mr-1.5" /> Sync tous les MOMA <span className="ml-1 text-[9px] uppercase tracking-wider">Expert</span></>
                  )}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={() => setLogsOpen(true)}
                variant="ghost"
                data-testid="nayax-logs-btn"
                className="text-[#575A57]"
                title="Historique des synchronisations"
              >
                <History className="w-3.5 h-3.5 mr-1.5" /> Historique
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setAutoMapOpen(true)}
                variant="outline"
                data-testid="nayax-automap-btn"
                title="Détecter automatiquement le lien MachineID Nayax ↔ Distributeur AgroManage"
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> Auto-mapper machines
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setProductAutoMapOpen(true)}
                variant="outline"
                data-testid="nayax-product-automap-btn"
                title="Auto-détecte le NayaxProductID pour chaque produit AgroManage (Phase 98al)"
                className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#E8F2EC]"
              >
                <Package className="w-3.5 h-3.5 mr-1.5" /> Auto-mapper produits
              </Button>
            </>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() => runTest()}
            disabled={running}
            variant="outline"
            data-testid="nayax-test-btn"
          >
            {running ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Test…</> : "Tester la connexion"}
          </Button>
        </div>
      </div>
      {!configured && !result && (
        <div className="mt-3 p-3 bg-[#FBF4E9] border border-[#E8D3A8] rounded text-[11px] text-[#8C6B2F] leading-relaxed">
          <p className="font-semibold mb-1">Comment obtenir votre Bearer token Nayax :</p>
          <ol className="list-decimal pl-4 space-y-0.5">
            <li>
              Connectez-vous à{" "}
              <a
                href="https://core.nayax.com"
                target="_blank"
                rel="noreferrer"
                className="underline inline-flex items-center gap-0.5"
              >
                Nayax Core (Back Office) <ExternalLink className="w-2.5 h-2.5" />
              </a>{" "}
              <span className="italic">— c&apos;est ici qu&apos;on génère le token, PAS sur le portail Lynx</span>
            </li>
            <li>Cliquez sur votre nom (en haut à droite) → <strong>Account Settings</strong></li>
            <li>Onglet <strong>Security and Login</strong> → section <strong>User Tokens</strong></li>
            <li>Cliquez <strong>Show Token</strong> et copiez la chaîne complète (attention au copier-coller, pas de saut de ligne)</li>
            <li>
              Ajoutez la variable <code className="font-mono bg-white px-1">NAYAX_API_KEY</code> via{" "}
              <strong>Emergent → Secrets</strong> (production) ou dans{" "}
              <code className="font-mono bg-white px-1">/app/backend/.env</code> (preview),
              puis redémarrez le backend
            </li>
            <li>
              Optionnel : ajoutez <code className="font-mono bg-white px-1">NAYAX_ENV=qa</code> pour
              tester d&apos;abord en sandbox QA (base URL{" "}
              <code className="font-mono bg-white px-1">qa-lynx.nayax.com/operational/api/v1</code>)
            </li>
            <li>Revenez ici et cliquez <strong>Tester la connexion</strong></li>
          </ol>
          <p className="mt-1.5 text-[10px] italic">
            Important : votre compte Nayax doit avoir le rôle <strong>Operator</strong> (permissions
            Lynx) activé par Nayax Support. Sans ça, même un token valide retourne 401/403.
          </p>
        </div>
      )}
      {renderResult()}
      {configured && (
        <div className="mt-3 p-3 bg-[#F4F4F0] border border-[#E5E5E0] rounded space-y-3">
          <div>
            <p className="text-[11px] font-semibold text-[#1A1C1A] mb-1.5">
              Tester avec un ActorID (Operator ID Nayax)
            </p>
            <p className="text-[10px] text-[#575A57] mb-2 leading-relaxed">
              L&apos;endpoint <code className="font-mono bg-white px-1">GET /devices</code> seul est
              redirigé (302). En passant votre ActorID Maison Thaï (ex.{" "}
              <code className="font-mono bg-white px-1">2002077194</code>), l&apos;API retourne
              <strong> la liste complète des machines de votre opérateur</strong>. Pour automatiser,
              définissez <code className="font-mono bg-white px-1">NAYAX_ACTOR_ID</code> dans Emergent Secrets.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={testActorId}
                onChange={(e) => setTestActorId(e.target.value)}
                placeholder="ex: 2002077194"
                className="flex-1 text-xs font-mono px-2 py-1 border border-[#E5E5E0] rounded bg-white focus:outline-none focus:border-[#1F4E3D]"
                data-testid="nayax-test-actorid-input"
                disabled={running}
              />
              <Button
                type="button"
                size="sm"
                onClick={() => runTest({ actorId: testActorId })}
                disabled={running || !testActorId.trim()}
                variant="outline"
                data-testid="nayax-test-actorid-btn"
              >
                {running ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Test…</> : "Lister les machines"}
              </Button>
            </div>
          </div>
          <div className="border-t border-[#E5E5E0] pt-3">
            <p className="text-[11px] font-semibold text-[#1A1C1A] mb-1.5">
              Tester avec un MachineID / DeviceID précis
            </p>
            <p className="text-[10px] text-[#575A57] mb-2 leading-relaxed">
              Pour vérifier qu&apos;une machine spécifique remonte correctement. Récupérez l&apos;ID
              depuis Nayax Core → <strong>Operations → Machines</strong>.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={testDeviceId}
                onChange={(e) => setTestDeviceId(e.target.value)}
                placeholder="MachineID / DeviceID (ex: 202180937)"
                className="flex-1 text-xs font-mono px-2 py-1 border border-[#E5E5E0] rounded bg-white focus:outline-none focus:border-[#1F4E3D]"
                data-testid="nayax-test-deviceid-input"
                disabled={running}
              />
              <Button
                type="button"
                size="sm"
                onClick={() => runTest({ deviceId: testDeviceId })}
                disabled={running || !testDeviceId.trim()}
                variant="outline"
                data-testid="nayax-test-deviceid-btn"
              >
                {running ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Test…</> : "Tester ce DeviceID"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <NayaxAutoMapDialog
        open={autoMapOpen}
        onOpenChange={setAutoMapOpen}
        onApplied={() => {
          if (onAfterMapping) onAfterMapping();
        }}
      />
      <NayaxProductAutoMapDialog
        open={productAutoMapOpen}
        onOpenChange={setProductAutoMapOpen}
        onApplied={() => {
          if (onAfterMapping) onAfterMapping();
        }}
      />
      <NayaxSyncLogsDialog open={logsOpen} onOpenChange={setLogsOpen} />

      {/* Phase 98ak — Confirm dialog du rattrapage sync MOMA */}
      <AlertDialog open={resyncConfirmOpen} onOpenChange={setResyncConfirmOpen}>
        <AlertDialogContent data-testid="nayax-resync-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rattraper la sync Nayax MOMA — tous distributeurs</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px] text-[#575A57]">
                <p>
                  Va rejouer la sync AgroManage → Nayax MOMA pour <strong>chaque distributeur
                  AUTOMATIQUE ayant un MachineID configuré</strong>, en utilisant les quantités
                  de leur <strong>dernière tournée verrouillée</strong>.
                </p>
                <p className="text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D3A8] rounded p-2 text-[12px]">
                  ⚠ À utiliser <strong>après configuration tardive</strong> d&apos;un
                  <code className="font-mono bg-white px-1 mx-1">nayax_machine_id</code> : les
                  tournées verrouillées avant l&apos;ajout de l&apos;ID n&apos;avaient pas pu être
                  poussées. Cette action pousse les mêmes quantités que la tournée initiale
                  (<strong>opération additive</strong> : si vous cliquez 2 fois, MOMA recevra la
                  quantité 2 fois).
                </p>
                <p className="text-[12px]">
                  Traçabilité intégrale dans <em>Historique des syncs</em> (source
                  <code className="font-mono bg-white px-1 mx-0.5">resync-all-latest-tour</code>).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="nayax-resync-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={runResyncAllMomas}
              className="bg-[#8C6B2F] hover:bg-[#735623] text-white"
              data-testid="nayax-resync-confirm-btn"
            >
              Lancer le rattrapage
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 98ak — Report dialog après rattrapage */}
      <Dialog open={resyncReportOpen} onOpenChange={setResyncReportOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="nayax-resync-report-dialog">
          <DialogHeader>
            <DialogTitle>Rapport de rattrapage Nayax MOMA</DialogTitle>
            <DialogDescription>
              Détail par distributeur — statistiques agrégées et résultats individuels.
            </DialogDescription>
          </DialogHeader>
          {resyncReport && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 bg-[#F4F4F0] rounded">
                  <div className="text-[10px] uppercase text-[#575A57]">Total</div>
                  <div className="text-lg font-semibold">{resyncReport.total_distributors}</div>
                </div>
                <div className="p-2 bg-[#E8F2EC] rounded">
                  <div className="text-[10px] uppercase text-[#1F4E3D]">OK</div>
                  <div className="text-lg font-semibold text-[#1F4E3D]">{resyncReport.success_count}</div>
                </div>
                <div className="p-2 bg-[#FBF4E9] rounded">
                  <div className="text-[10px] uppercase text-[#8C6B2F]">Ignorés</div>
                  <div className="text-lg font-semibold text-[#8C6B2F]">{resyncReport.skipped_count}</div>
                </div>
                <div className="p-2 bg-[#F9E4DF] rounded">
                  <div className="text-[10px] uppercase text-[#C05C47]">Échecs</div>
                  <div className="text-lg font-semibold text-[#C05C47]">{resyncReport.failed_count}</div>
                </div>
              </div>
              {resyncReport.message && (
                <div className="text-[12px] text-[#575A57] italic p-2 bg-[#F4F4F0] rounded">
                  {resyncReport.message}
                </div>
              )}
              {/* Bannière globale : si TOUS les distribs sont ignorés à cause du mapping manquant */}
              {(() => {
                const rs = resyncReport.results || [];
                const allSkippedForMapping = rs.length > 0 && rs.every((r) =>
                  r.skipped && (r.missing_mapping?.length || 0) > 0
                );
                if (!allSkippedForMapping) return null;
                // Agrège tous les produits uniques à mapper
                const byLabel = new Map();
                for (const r of rs) {
                  for (const m of r.missing_mapping || []) {
                    byLabel.set(m.label, m);
                  }
                }
                const uniqueMissing = Array.from(byLabel.values());
                return (
                  <div
                    className="p-3 bg-[#FBF4E9] border border-[#E8D3A8] rounded text-[12px] text-[#8C6B2F] leading-relaxed"
                    data-testid="nayax-resync-mapping-cta"
                  >
                    <p className="font-semibold mb-1">
                      🔧 Rien à pousser : le champ <code className="font-mono bg-white px-1">nayax_product_id</code> manque sur les produits des tournées.
                    </p>
                    <p className="mb-1.5">
                      Ce champ fait le lien entre chaque produit AgroManage et son article dans le catalogue Nayax MOMA. Sans lui, la sync ne sait pas vers quel slot pousser.
                    </p>
                    <p className="mb-1.5">
                      <strong>Produit(s) unique(s) à mapper</strong> ({uniqueMissing.length}) :
                    </p>
                    <ul className="list-disc pl-4 space-y-0.5 mb-2">
                      {uniqueMissing.slice(0, 10).map((m) => (
                        <li key={m.product_id} data-testid={`nayax-resync-missing-${m.product_id}`}>
                          <span className="font-mono">{m.sku || "?"}</span> — {m.name || m.label}
                        </li>
                      ))}
                      {uniqueMissing.length > 10 && (
                        <li>… +{uniqueMissing.length - 10} autres</li>
                      )}
                    </ul>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href="/produits"
                        className="inline-block bg-[#8C6B2F] hover:bg-[#735623] text-white text-[11px] px-3 py-1.5 rounded"
                        data-testid="nayax-resync-goto-products-btn"
                      >
                        Aller aux Produits →
                      </a>
                      <span className="text-[10px] italic">
                        Ouvre chaque fiche produit et renseigne le champ <strong>NayaxProductID</strong>, puis relance « Sync tous les MOMA ».
                      </span>
                    </div>
                  </div>
                );
              })()}
              <div className="border border-[#E5E5E0] rounded overflow-hidden" data-testid="nayax-resync-report-rows">
                {(resyncReport.results || []).map((r, idx) => {
                  const status = r.ok
                    ? { color: "bg-[#E8F2EC] text-[#1F4E3D]", label: "OK" }
                    : r.skipped
                    ? { color: "bg-[#FBF4E9] text-[#8C6B2F]", label: "Ignoré" }
                    : { color: "bg-[#F9E4DF] text-[#C05C47]", label: "Échec" };
                  return (
                    <div
                      key={r.distributor_id || idx}
                      className={`p-2 flex items-start justify-between gap-3 text-[12px] ${idx > 0 ? "border-t border-[#E5E5E0]" : ""}`}
                      data-testid={`nayax-resync-report-row-${r.distributor_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1A1C1A] truncate">{r.distributor_name || r.distributor_id}</div>
                        <div className="text-[10px] text-[#575A57] font-mono truncate">
                          MachineID {r.nayax_machine_id || "?"}
                          {r.tour_id && ` · Tournée ${r.tour_id.slice(0, 8)}…`}
                        </div>
                        {r.reason && (
                          <div className="text-[10px] text-[#8C6B2F] mt-0.5 italic">{r.reason}</div>
                        )}
                        {r.ok && r.products_count != null && (
                          <div className="text-[10px] text-[#1F4E3D] mt-0.5">
                            📡 {r.products_count} produit(s) poussé(s) — HTTP {r.http_status}
                          </div>
                        )}
                        {!r.ok && !r.skipped && (
                          <div className="text-[10px] text-[#C05C47] mt-0.5">
                            {r.error || r.step || `HTTP ${r.http_status || "?"}`}
                          </div>
                        )}
                        {!r.ok && !r.skipped && (
                          <button
                            type="button"
                            className="mt-1 text-[10px] underline text-[#8C6B2F] hover:text-[#6B5024] disabled:opacity-50"
                            disabled={debugRunning}
                            onClick={() => runDebugOneByOne(r.distributor_id, r.tour_id)}
                            data-testid={`nayax-debug-1by1-btn-${r.distributor_id}`}
                          >
                            {debugRunningFor === r.distributor_id ? "Debug en cours…" : "🔬 Debug 1-par-1 (isoler le produit fautif)"}
                          </button>
                        )}
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded whitespace-nowrap ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setResyncReportOpen(false)}
              data-testid="nayax-resync-report-close-btn"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 101ay — Rapport Debug 1-par-1 (isolation produit fautif Nayax) */}
      <Dialog open={debugReportOpen} onOpenChange={setDebugReportOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="nayax-debug-report-dialog">
          <DialogHeader>
            <DialogTitle>Debug 1-par-1 — {debugReport?.distributor_name || "…"}</DialogTitle>
            <DialogDescription>
              Chaque produit de la tournée a été envoyé isolément à Nayax MOMA. Les lignes rouges
              sont celles qui font crasher l&apos;API (HTTP 500 générique côté Nayax).
            </DialogDescription>
          </DialogHeader>
          {debugReport && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-[12px]">
                <div className="bg-[#F4F4F0] p-2 rounded">
                  <div className="text-[10px] text-[#575A57] uppercase">Total testés</div>
                  <div className="text-lg font-semibold">{debugReport.total_tested}</div>
                </div>
                <div className="bg-[#E8F2EC] p-2 rounded">
                  <div className="text-[10px] text-[#575A57] uppercase">OK</div>
                  <div className="text-lg font-semibold text-[#1F4E3D]">{debugReport.ok_count}</div>
                </div>
                <div className="bg-[#F9E4DF] p-2 rounded">
                  <div className="text-[10px] text-[#575A57] uppercase">En échec</div>
                  <div className="text-lg font-semibold text-[#C05C47]">{debugReport.failed_count}</div>
                </div>
                <div className="bg-[#FBF4E9] p-2 rounded">
                  <div className="text-[10px] text-[#575A57] uppercase">Ignorés</div>
                  <div className="text-lg font-semibold text-[#8C6B2F]">{debugReport.skipped_count}</div>
                </div>
              </div>

              {(debugReport.failing_products || []).length > 0 && (
                <div className="border-l-4 border-[#C05C47] bg-[#FDF5F2] p-3 rounded" data-testid="nayax-debug-failing-products">
                  <div className="text-[13px] font-semibold text-[#C05C47] mb-2">
                    🔴 {debugReport.failing_products.length} produit(s) fautif(s) — à corriger côté Nayax ou côté mapping AM
                  </div>
                  <ul className="text-[11px] space-y-1 text-[#1A1C1A]">
                    {debugReport.failing_products.map((f) => (
                      <li key={f.product_id} className="font-mono">
                        <span className="font-semibold">{f.sku || f.name}</span> · NPID {f.nayax_product_id}
                        {f.machine_product_id ? ` · Slot ${f.machine_product_id}` : ""}
                        {f.par_nayax != null ? ` · PAR ${f.par_nayax}` : ""}
                        {" · "}<span className="text-[#C05C47]">HTTP {f.http_status || "?"}</span>
                        {f.correlation_id && (
                          <span className="ml-1 text-[#8C6B2F]">· Correlation {f.correlation_id}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="text-[10px] text-[#575A57] mt-2 italic">
                    Correlation ID à communiquer au support Nayax si le problème n&apos;est pas résolu côté mapping AM.
                  </div>
                </div>
              )}

              <div className="border border-[#E5E5E0] rounded overflow-hidden" data-testid="nayax-debug-rows">
                {(debugReport.tests || []).map((t, idx) => {
                  const status = t.ok
                    ? { color: "bg-[#E8F2EC] text-[#1F4E3D]", label: "OK" }
                    : t.skipped
                    ? { color: "bg-[#FBF4E9] text-[#8C6B2F]", label: "Ignoré" }
                    : { color: "bg-[#F9E4DF] text-[#C05C47]", label: "Échec" };
                  return (
                    <div
                      key={t.product_id || idx}
                      className={`p-2 flex items-start justify-between gap-3 text-[11px] ${idx > 0 ? "border-t border-[#E5E5E0]" : ""}`}
                      data-testid={`nayax-debug-row-${t.product_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1A1C1A] truncate">
                          {t.sku || "?"} · {t.name || "?"}
                        </div>
                        <div className="text-[10px] text-[#575A57] font-mono">
                          NPID {t.nayax_product_id ?? "—"}
                          {t.machine_product_id != null && ` · MPID ${t.machine_product_id}`}
                          {t.par_nayax != null && ` · PAR ${t.par_nayax}`}
                          {" · Qty "}{t.pickqty_sent ?? t.pickqty_requested ?? 0}
                          {t.pickqty_sent != null && t.pickqty_requested != null && t.pickqty_sent !== t.pickqty_requested && (
                            <span className="text-[#8C6B2F]"> (cappé depuis {t.pickqty_requested})</span>
                          )}
                        </div>
                        {t.reason && (
                          <div className="text-[10px] text-[#8C6B2F] mt-0.5 italic">{t.reason}</div>
                        )}
                        {!t.ok && !t.skipped && (
                          <div className="text-[10px] text-[#C05C47] mt-0.5">
                            HTTP {t.http_status || "?"} — {t.error || "?"}
                            {t.correlation_id && <span className="ml-1 text-[#8C6B2F]">(cid: {t.correlation_id})</span>}
                          </div>
                        )}
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded whitespace-nowrap ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setDebugReportOpen(false)}
              data-testid="nayax-debug-report-close-btn"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
